import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as PIXI from 'pixi.js';
import { useScreenShare } from '@daily-co/daily-react';
//Recoil系
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import { screenShareAtom } from '../../State';
import DetectScreenShare from './DetectScreenShare';
import { useTranslation } from 'react-i18next';

export default function ScreenShare({ pixiRef, canvasRef, canvasHeight, canvasWidth, wrapRef }) {
  const [screenShareState, setScreenShareState] = useRecoilState(screenShareAtom);
  const { stopScreenShare } = useScreenShare(); //スクリーンシェア
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const videoResourceRef = useRef(null);
  const videoWrapRef = useRef(null);
  const videoSpriteRef = useRef(null);
  const screenSpriteRef = useRef(null);
  const containerRef = useRef(null);
  const videoHeightRef = useRef(null);
  const videoWidthRef = useRef(null);
  const audioRef = useRef(null); //音声リソース
  const workerRef = useRef(null);

  useEffect(() => {
    setBackgroundProcess();
  }, []);

  //スクリーン初期化
  useEffect(() => {
    //スクリーンシェア開始
    if (videoRef.current != null && screenShareState != null) {
      //スクリーン初期化
      initScreen();
      //位置の調整
      adjustScreen();

      //スクリーンシェア中に参加した時に表示されない問題の解消（adjustScreenを良いタイミングで呼べば回避できる）
      setTimeout(() => {
        adjustScreen();
      }, 2100);
    }
    //スクリーンシェア停止
    if (screenShareState == null && containerRef.current != null) {
      //一度ここでキャンバスサイズを変更しておく

      //スクリーンん削除
      containerRef.current.parent.removeChild(containerRef.current);
      containerRef.current = null;

      pixiRef.current.resize(wrapRef.current.offsetWidth, wrapRef.current.offsetHeight);
    }
  }, [screenShareState]);

  //調整
  useEffect(() => {
    if (containerRef.current != null) {
      setTimeout(() => {
        adjustScreen();
      }, 150);
    }
  }, [canvasWidth, canvasHeight, screenShareState]);

  const stopSharing = () => {
    stopScreenShare();
  };

  //バックグランド処理のリスナー
  function setBackgroundProcess() {
    // ページの表示状態が変わったときに実行
    document.addEventListener('visibilitychange', function () {
      let status = document.visibilityState;

      //ブラウザがバックグランドへ行った
      if (status == 'hidden') {
        workerRef.current = new Worker('js/worker.js');

        //Workerにメッセージを送信(Worker処理開始)
        workerRef.current.postMessage('start');

        workerRef.current.addEventListener(
          'message',
          function (e) {
            let diff = e.data;
            if (videoResourceRef.current != null) {
              videoResourceRef.current.update(diff);
            }
          },
          false,
        );
      }
      //ブラウザが再度アクティブになった
      else {
        if (workerRef.current != null) {
          workerRef.current.terminate();
          workerRef.current = null;
        }
      }
    });
  }

  //スクリーン初期化
  function initScreen() {
    //一度ここでキャンバスサイズを変更しておく
    pixiRef.current.resize(wrapRef.current.offsetWidth, wrapRef.current.offsetHeight);

    //メディアトラックをビデオリソースに追加
    videoRef.current.srcObject = new MediaStream([screenShareState.persistentTrack]);

    //音声
    if (screenShareState.audioTrack != null) {
      audioRef.current.srcObject = new MediaStream([screenShareState.audioTrack]);
    }

    //ビデオリソースからPIXIにわたす
    videoResourceRef.current = new PIXI.VideoResource(videoRef.current);
    videoResourceRef.current.updateFPS = 30; // set video to render at 10FPS to avoid performance issues

    let texture = new PIXI.Texture.from(videoResourceRef.current);

    //サイズを格納
    videoWidthRef.current = texture.orig.width;
    videoHeightRef.current = texture.orig.height;

    videoSpriteRef.current = new PIXI.Sprite(texture);

    //コンテイナー
    containerRef.current = new PIXI.Container();

    //四角のグラフィック作成
    const r1 = new PIXI.Graphics();
    r1.beginFill(0x000000);
    r1.drawRect(0, 0, 10, 10);
    r1.endFill();

    //テキスチャに変換
    var t1 = pixiRef.current.renderer.generateTexture(r1);

    //背景スプライト追加
    screenSpriteRef.current = new PIXI.Sprite(t1);

    //コンテイナーに追加
    containerRef.current.addChild(screenSpriteRef.current);

    containerRef.current.addChild(videoSpriteRef.current);
    pixiRef.current.stage.addChild(containerRef.current);

    //ソート順序を変更
    containerRef.current.zIndex = -1;
  }

  //位置調整
  function adjustScreen() {
    if (screenShareState == null) return;
    if (screenShareState.persistentTrack == null) return;

    //シャアされた動画自体の高さと幅
    var screenVideoHeight;
    var screenVideoWidth;

    //getSettingsが取得できるかどうか
    if (typeof screenShareState.persistentTrack.getSettings() !== 'undefined') {
      var videoInfo = screenShareState.persistentTrack.getSettings();

      //サイズが取得できない場合はもう一度チャレンジ 1秒後に
      if (videoInfo.width == null) {
        setTimeout(() => {
          adjustScreen();
        }, 1000);
        return;
      }

      screenVideoWidth = videoInfo.width ?? wrapRef.current.offsetWidth;
      screenVideoHeight = videoInfo.height ?? wrapRef.current.offsetHeight;
    } else {
      screenVideoWidth = wrapRef.current.offsetWidth;
      screenVideoHeight = wrapRef.current.offsetHeight;
    }

    let videoHeight = (wrapRef.current.offsetWidth * screenVideoHeight) / screenVideoWidth;

    let finalWidth;
    let finalHeight;

    if (videoHeight > wrapRef.current.offsetHeight) {
      finalHeight = wrapRef.current.offsetHeight;
      finalWidth = (wrapRef.current.offsetHeight * screenVideoWidth) / screenVideoHeight;
    } else {
      finalHeight = videoHeight;
      finalWidth = videoRef.current.offsetWidth;
    }

    //スクリーン動画の位置調整
    videoSpriteRef.current.width = finalWidth;
    videoSpriteRef.current.height = finalHeight;
    videoSpriteRef.current.anchor.set(0.5, 0.5);
    videoSpriteRef.current.x = canvasRef.current.offsetWidth / 2;
    videoSpriteRef.current.y = canvasRef.current.offsetHeight / 2;

    //背景の位置調整
    screenSpriteRef.current.height = canvasRef.current.offsetHeight;
    screenSpriteRef.current.width = canvasRef.current.offsetWidth;
    screenSpriteRef.current.anchor.set(0.5, 0.5);
    screenSpriteRef.current.x = canvasRef.current.offsetWidth / 2;
    screenSpriteRef.current.y = canvasRef.current.offsetHeight / 2;
  }

  //レンダービュー
  return (
    <>
      {/*スクリーンシェア検出用 */}
      <DetectScreenShare />

      {/*スクリーンシェア中の場合 */}
      {screenShareState != null && (
        <>
          {screenShareState.isWholeScreen && (
            <div id="isWholeScreenWrap">
              <div id="isWholeScreenInside">
                <div> {t('bottom.Sharing whole screeen')}</div>
                <button onClick={stopSharing}>{t('bottom.Stop sharing')}</button>
              </div>
            </div>
          )}
          <div id="screenDummy" ref={videoWrapRef} style={{ visibility: 'hidden' }}>
            <video autoPlay muted playsInline ref={videoRef} style={{ visibility: 'hidden' }} />
            <audio autoPlay playsInline id="screenAudio111" ref={audioRef} />
          </div>
        </>
      )}
    </>
  );
}
